import { useState, useEffect, useCallback, useRef } from 'react';

// ─── Types ───────────────────────────────────────────────────────────

export interface LatencyPoint {
  timestamp: number;
  p50: number;
  p95: number;
  p99: number;
}

export interface AgentLatency {
  name: string;
  avgLatency: number;
  calls: number;
}

export interface CallEvent {
  id: string;
  type: 'stt' | 'agent' | 'deflection' | 'tts' | 'new_call' | 'network' | 'resolution';
  message: string;
  timestamp: number;
  detail?: Record<string, unknown>;
}

export interface DeflectionTrend {
  date: string;
  rate: number;
  movingAvg: number;
}

export interface IssueDeflection {
  issue: string;
  rate: number;
}

export interface FunnelStage {
  name: string;
  value: number;
  percentage: number;
}

export interface ToolSuccess {
  tool: string;
  successRate: number;
}

export interface ActiveAgent {
  name: string;
  count: number;
  status: 'healthy' | 'warning' | 'critical';
}

export interface SystemMetrics {
  concurrentCalls: number;
  maxCapacity: number;
  successRate: number;
  toolSuccess: ToolSuccess[];
  activeAgents: ActiveAgent[];
}

export interface MetricsData {
  sttLatency: LatencyPoint[];
  agentLatency: AgentLatency[];
  ttsLatency: LatencyPoint[];
  callEvents: CallEvent[];
  deflectionTrend: DeflectionTrend[];
  issueDeflection: IssueDeflection[];
  funnel: FunnelStage[];
  system: SystemMetrics;
}

// ─── Helpers ─────────────────────────────────────────────────────────

/** Clamp a value between min and max */
function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/** Random walk: previous value + small random delta, then clamped */
function randomWalk(prev: number, min: number, max: number, maxDelta = 15): number {
  const delta = (Math.random() - 0.5) * 2 * maxDelta;
  return clamp(prev + delta, min, max);
}

/** Format ms as readable string */
export function formatMs(ms: number): string {
  return `${Math.round(ms)}ms`;
}

/** Generate a call ID */
let callIdCounter = 28471;
function nextCallId(): string {
  callIdCounter++;
  return `#${callIdCounter}`;
}

// ─── Initial Data ────────────────────────────────────────────────────

function generateInitialSTTLatency(): LatencyPoint[] {
  const data: LatencyPoint[] = [];
  let p50 = 145;
  let p95 = 280;
  let p99 = 350;
  const now = Date.now();
  for (let i = 30; i >= 0; i--) {
    p50 = randomWalk(p50, 100, 250, 20);
    p95 = randomWalk(p95, 200, 380, 25);
    p99 = randomWalk(p99, 250, 450, 30);
    data.push({
      timestamp: now - i * 2000,
      p50: Math.round(p50),
      p95: Math.round(p95),
      p99: Math.round(p99),
    });
  }
  return data;
}

function generateInitialTTSLatency(): LatencyPoint[] {
  const data: LatencyPoint[] = [];
  let p50 = 180;
  let p95 = 290;
  let p99 = 340;
  const now = Date.now();
  for (let i = 30; i >= 0; i--) {
    p50 = randomWalk(p50, 120, 280, 18);
    p95 = randomWalk(p95, 200, 380, 22);
    p99 = randomWalk(p99, 230, 420, 28);
    data.push({
      timestamp: now - i * 2000,
      p50: Math.round(p50),
      p95: Math.round(p95),
      p99: Math.round(p99),
    });
  }
  return data;
}

function generateDeflectionTrend(): DeflectionTrend[] {
  const data: DeflectionTrend[] = [];
  let rate = 72;
  const now = Date.now();
  for (let i = 29; i >= 0; i--) {
    rate = randomWalk(rate, 65, 82, 3);
    const date = new Date(now - i * 86400000);
    // Calculate 7-day moving average
    const windowStart = Math.max(0, data.length - 6);
    const window = [...data.slice(windowStart), { rate } as DeflectionTrend];
    const movingAvg = window.reduce((s, d) => s + d.rate, 0) / window.length;
    data.push({
      date: date.toISOString().split('T')[0],
      rate: Math.round(rate * 10) / 10,
      movingAvg: Math.round(movingAvg * 10) / 10,
    });
  }
  return data;
}

function generateAgentLatency(): AgentLatency[] {
  return [
    { name: 'AuthAgent', avgLatency: Math.round(randomWalk(320, 200, 500, 30)), calls: 1247 },
    { name: 'NetworkAgent', avgLatency: Math.round(randomWalk(480, 300, 700, 40)), calls: 892 },
    { name: 'TicketAgent', avgLatency: Math.round(randomWalk(390, 250, 600, 35)), calls: 634 },
    { name: 'SupervisorAgent', avgLatency: Math.round(randomWalk(520, 350, 800, 45)), calls: 312 },
  ];
}

function generateFunnel(): FunnelStage[] {
  return [
    { name: 'Total Calls', value: 100, percentage: 100 },
    { name: 'AI Handled', value: 91, percentage: 91 },
    { name: 'AI Resolved', value: 78.5, percentage: 86.3 },
    { name: 'Human Escalated', value: 21.5, percentage: 21.5 },
  ];
}

function generateIssueDeflection(): IssueDeflection[] {
  return [
    { issue: 'Billing', rate: 85 },
    { issue: 'Technical', rate: 72 },
    { issue: 'Account', rate: 68 },
    { issue: 'Network', rate: 91 },
    { issue: 'Sales', rate: 95 },
  ];
}

function generateToolSuccess(): ToolSuccess[] {
  return [
    { tool: 'get_customer', successRate: 99.9 },
    { tool: 'check_outage', successRate: 99.5 },
    { tool: 'create_ticket', successRate: 99.8 },
    { tool: 'get_call_history', successRate: 99.9 },
  ];
}

function generateActiveAgents(): ActiveAgent[] {
  return [
    { name: 'AuthAgent', count: 12, status: 'healthy' },
    { name: 'NetworkAgent', count: 8, status: 'healthy' },
    { name: 'TicketAgent', count: 6, status: 'healthy' },
    { name: 'SupervisorAgent', count: 4, status: 'healthy' },
  ];
}

// ─── Event Generator ─────────────────────────────────────────────────

const eventTemplates: Array<() => { type: CallEvent['type']; message: string; detail?: Record<string, unknown> }> = [
  () => ({
    type: 'stt',
    message: `STT latency: ${Math.round(randomWalk(142, 100, 250, 20))}ms (p50) | ${Math.round(randomWalk(287, 200, 380, 25))}ms (p95)`,
    detail: { component: 'stt' },
  }),
  () => {
    const agents = ['AuthAgent', 'NetworkAgent', 'TicketAgent', 'SupervisorAgent'];
    const agent = agents[Math.floor(Math.random() * agents.length)];
    return {
      type: 'agent',
      message: `Call ${nextCallId()}: ${agent} resolved in ${(Math.random() * 2 + 0.5).toFixed(1)}s`,
      detail: { agent },
    };
  },
  () => {
    const rate = randomWalk(78.3, 75, 82, 0.5);
    const delta = (Math.random() * 0.3 - 0.1).toFixed(1);
    return {
      type: 'deflection',
      message: `Deflection rate: ${rate.toFixed(1)}% (${Number(delta) >= 0 ? '+' : ''}${delta}%)`,
      detail: { rate },
    };
  },
  () => ({
    type: 'tts',
    message: `TTS chunk: ${Math.round(randomWalk(320, 150, 380, 30))}ms (voice: vokentra_en)`,
    detail: { component: 'tts' },
  }),
  () => {
    const reasons = ['billing inquiry', 'technical support', 'network issue', 'account query', 'new connection'];
    return {
      type: 'new_call',
      message: `New call: ${nextCallId()} (${reasons[Math.floor(Math.random() * reasons.length)]})`,
      detail: { callId: callIdCounter },
    };
  },
  () => {
    const areas = ['SW1A', 'EC2A', 'W1B', 'M1', 'B1', 'LS1'];
    return {
      type: 'network',
      message: `NetworkAgent: outage detected in ${areas[Math.floor(Math.random() * areas.length)]}`,
      detail: { severity: Math.random() > 0.7 ? 'high' : 'low' },
    };
  },
  () => {
    const resolutions = ['account_unlocked', 'ticket_created', 'line_reset', 'payment_processed', 'plan_changed'];
    return {
      type: 'resolution',
      message: `Resolution: ${resolutions[Math.floor(Math.random() * resolutions.length)]} | customer: C-${Math.floor(Math.random() * 90000 + 10000)}`,
      detail: {},
    };
  },
];

function generateEvent(): CallEvent {
  const template = eventTemplates[Math.floor(Math.random() * eventTemplates.length)];
  const { type, message, detail } = template();
  return {
    id: `evt-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    type,
    message,
    timestamp: Date.now(),
    detail,
  };
}

// ─── Hook ────────────────────────────────────────────────────────────

export function useMetricsData(updateInterval = 2000) {
  const [sttLatency, setSttLatency] = useState<LatencyPoint[]>(generateInitialSTTLatency);
  const [ttsLatency, setTtsLatency] = useState<LatencyPoint[]>(generateInitialTTSLatency);
  const [agentLatency, setAgentLatency] = useState<AgentLatency[]>(generateAgentLatency);
  const [callEvents, setCallEvents] = useState<CallEvent[]>(() => {
    const events: CallEvent[] = [];
    for (let i = 0; i < 20; i++) {
      const evt = generateEvent();
      evt.timestamp = Date.now() - (20 - i) * 1200;
      events.push(evt);
    }
    return events;
  });
  const [deflectionTrend, setDeflectionTrend] = useState<DeflectionTrend[]>(generateDeflectionTrend);
  const [issueDeflection] = useState<IssueDeflection[]>(generateIssueDeflection);
  const [funnel] = useState<FunnelStage[]>(generateFunnel);
  const [system, setSystem] = useState<SystemMetrics>({
    concurrentCalls: 247,
    maxCapacity: 1000,
    successRate: 99.7,
    toolSuccess: generateToolSuccess(),
    activeAgents: generateActiveAgents(),
  });

  // Track latest values in refs for the interval callback
  const sttRef = useRef(sttLatency);
  const ttsRef = useRef(ttsLatency);
  const agentRef = useRef(agentLatency);
  const eventsRef = useRef(callEvents);
  const systemRef = useRef(system);

  sttRef.current = sttLatency;
  ttsRef.current = ttsLatency;
  agentRef.current = agentLatency;
  eventsRef.current = callEvents;
  systemRef.current = system;

  const tick = useCallback(() => {
    const now = Date.now();

    // ── Update STT latency ──
    setSttLatency((prev) => {
      const last = prev[prev.length - 1];
      const next: LatencyPoint = {
        timestamp: now,
        p50: Math.round(randomWalk(last.p50, 100, 250, 25)),
        p95: Math.round(randomWalk(last.p95, 200, 380, 30)),
        p99: Math.round(randomWalk(last.p99, 250, 450, 35)),
      };
      const nextArr = [...prev.slice(1), next];
      sttRef.current = nextArr;
      return nextArr;
    });

    // ── Update TTS latency ──
    setTtsLatency((prev) => {
      const last = prev[prev.length - 1];
      const next: LatencyPoint = {
        timestamp: now,
        p50: Math.round(randomWalk(last.p50, 120, 280, 22)),
        p95: Math.round(randomWalk(last.p95, 200, 380, 28)),
        p99: Math.round(randomWalk(last.p99, 230, 420, 32)),
      };
      const nextArr = [...prev.slice(1), next];
      ttsRef.current = nextArr;
      return nextArr;
    });

    // ── Update agent latency ──
    setAgentLatency((prev) => {
      const next = prev.map((a) => ({
        ...a,
        avgLatency: Math.round(randomWalk(a.avgLatency, 200, 800, 40)),
        calls: a.calls + (Math.random() > 0.5 ? 1 : 0),
      }));
      agentRef.current = next;
      return next;
    });

    // ── Add new event ──
    setCallEvents((prev) => {
      const evt = generateEvent();
      const next = [evt, ...prev].slice(0, 100);
      eventsRef.current = next;
      return next;
    });

    // ── Update system metrics ──
    setSystem((prev) => {
      const next = {
        ...prev,
        concurrentCalls: Math.round(clamp(prev.concurrentCalls + (Math.random() - 0.48) * 8, 180, 340)),
        successRate: Math.round(clamp(prev.successRate + (Math.random() - 0.5) * 0.2, 98.5, 99.99) * 10) / 10,
        toolSuccess: prev.toolSuccess.map((t) => ({
          ...t,
          successRate: Math.round(clamp(t.successRate + (Math.random() - 0.5) * 0.15, 97, 100) * 10) / 10,
        })),
      };
      systemRef.current = next;
      return next;
    });

    // ── Update deflection trend (once per tick, shift by fraction) ──
    setDeflectionTrend((prev) => {
      const last = prev[prev.length - 1];
      const newRate = randomWalk(last.rate, 70, 84, 1.5);
      const newMovingAvg = prev.slice(-6).reduce((s, d) => s + d.rate, 0) / Math.min(7, prev.length);
      const today = new Date().toISOString().split('T')[0];
      const next = [...prev];
      next[next.length - 1] = {
        ...last,
        rate: Math.round(newRate * 10) / 10,
        movingAvg: Math.round(newMovingAvg * 10) / 10,
      };
      return next;
    });
  }, []);

  useEffect(() => {
    const interval = setInterval(tick, updateInterval);
    return () => clearInterval(interval);
  }, [tick, updateInterval]);

  return {
    sttLatency,
    ttsLatency,
    agentLatency,
    callEvents,
    deflectionTrend,
    issueDeflection,
    funnel,
    system,
  };
}

export default useMetricsData;
